import { IBac, IPlanningEntry } from '../../models';
import { getCurrentMonthISO } from '../shared/MonthUtils';

export type ActionType =
  | 'relance_depot'
  | 'info_clients'
  | 'intervention_imminente'
  | 'planning_manquant';

export type ActionSeverity = 'high' | 'medium' | 'low';

export interface IActionItem {
  id: string;
  type: ActionType;
  severity: ActionSeverity;
  title: string;
  description: string;
  depotTitle: string;
  bacTitle?: string;
  dueHint?: string;
  /** ID used for resolution (bacId, restrictionId…) */
  targetId: number;
  /** Raw data kept for the resolve callbacks to access. */
  bac?: IBac;
  planningEntry?: IPlanningEntry;
}

export const ACTION_LABELS: Record<ActionType, { label: string; icon: string; color: string }> = {
  relance_depot:          { label: 'Relance dépôt',          icon: '📞', color: '#F9A825' },
  info_clients:           { label: 'Communication client',   icon: '✉️', color: '#2B5EA7' },
  intervention_imminente: { label: 'Intervention imminente', icon: '⚠️', color: '#E53935' },
  planning_manquant:      { label: 'Planning à compléter',   icon: '📋', color: '#78909C' },
};

function monthsAhead(iso: string, count: number): string[] {
  const result: string[] = [];
  const d = new Date(iso);
  for (let i = 0; i < count; i++) {
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() + 1);
  }
  return result;
}

function diffDays(iso: string): number {
  const target = new Date(iso);
  const now = new Date();
  return Math.round((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export interface IComputeActionsInput {
  bacs: IBac[];
  planning: IPlanningEntry[];
}

export function computeActions(input: IComputeActionsInput): IActionItem[] {
  const { bacs, planning } = input;
  const actions: IActionItem[] = [];

  const currentMonth = getCurrentMonthISO().substring(0, 7);
  const nextThreeMonths = monthsAhead(getCurrentMonthISO(), 3);

  // Index planning by bacId + month for quick lookups.
  const planningByBacMonth: Record<string, IPlanningEntry> = {};
  for (const p of planning) {
    planningByBacMonth[`${p.BacId}-${p.Mois.substring(0, 7)}`] = p;
  }

  // 1. Interventions imminentes (< 30 jours) + dérivés (relance dépôt + info clients)
  for (const p of planning) {
    if (p.TypeContenu !== 'Intervention' && p.TypeContenu !== 'Transition') continue;
    const bac = bacs.find(b => b.Id === p.BacId);
    if (!bac) continue;

    const days = diffDays(p.Mois);
    if (days < -15 || days > 60) continue;

    const isCurrentOrNext = p.Mois.substring(0, 7) === currentMonth || days >= 0;
    if (!isCurrentOrNext) continue;

    // Intervention imminente : urgent si < 15 jours
    const sev: ActionSeverity = days <= 15 ? 'high' : days <= 30 ? 'medium' : 'low';
    actions.push({
      id: `intervention_imminente:${p.Id}`,
      type: 'intervention_imminente',
      severity: sev,
      title: `${p.Contenu} prévu sur ${bac.Title}`,
      description: `${bac.DepotTitle || 'Dépôt'} — ${p.Contenu} programmé${days <= 0 ? ' (en cours)' : ''}`,
      depotTitle: bac.DepotTitle || '',
      bacTitle: bac.Title,
      dueHint: days <= 0 ? 'En cours' : days === 1 ? 'Demain' : `Dans ${days} jours`,
      targetId: p.Id,
      bac,
      planningEntry: p,
    });

    // Relance dépôt si le point n'a pas été fait
    if (!bac.PointDepot) {
      actions.push({
        id: `relance_depot:${bac.Id}:${p.Id}`,
        type: 'relance_depot',
        severity: days <= 15 ? 'high' : 'medium',
        title: `Relancer ${bac.DepotTitle} pour le bac ${bac.Title}`,
        description: `Point avec le dépôt non fait avant ${p.Contenu}`,
        depotTitle: bac.DepotTitle || '',
        bacTitle: bac.Title,
        dueHint: days <= 0 ? 'En retard' : `Sous ${days} j`,
        targetId: bac.Id,
        bac,
        planningEntry: p,
      });
    }

    // Info clients non transmise
    if (!bac.InfoClients) {
      actions.push({
        id: `info_clients:${bac.Id}:${p.Id}`,
        type: 'info_clients',
        severity: days <= 15 ? 'high' : 'medium',
        title: `Communiquer aux clients — ${bac.DepotTitle} / ${bac.Title}`,
        description: `Information non transmise avant ${p.Contenu}`,
        depotTitle: bac.DepotTitle || '',
        bacTitle: bac.Title,
        dueHint: days <= 0 ? 'En retard' : `Sous ${days} j`,
        targetId: bac.Id,
        bac,
        planningEntry: p,
      });
    }
  }

  // 2. Bacs sans planning renseigné sur les 3 prochains mois
  for (const bac of bacs) {
    const missingMonths: string[] = [];
    for (const month of nextThreeMonths) {
      if (!planningByBacMonth[`${bac.Id}-${month}`]) {
        missingMonths.push(month);
      }
    }
    if (missingMonths.length >= 2) {
      actions.push({
        id: `planning_manquant:${bac.Id}`,
        type: 'planning_manquant',
        severity: missingMonths.length === 3 ? 'medium' : 'low',
        title: `Renseigner le planning — ${bac.DepotTitle} / ${bac.Title}`,
        description: `${missingMonths.length} mois sans information sur les 3 prochains`,
        depotTitle: bac.DepotTitle || '',
        bacTitle: bac.Title,
        targetId: bac.Id,
        bac,
      });
    }
  }

  // Sort: severity desc, then due date, then depot
  const sevOrder: Record<ActionSeverity, number> = { high: 0, medium: 1, low: 2 };
  actions.sort((a, b) => {
    if (sevOrder[a.severity] !== sevOrder[b.severity]) {
      return sevOrder[a.severity] - sevOrder[b.severity];
    }
    return a.depotTitle.localeCompare(b.depotTitle, 'fr');
  });

  return actions;
}
